import React, { useState, useEffect, useRef, useCallback } from 'react';
import ReactDOM from 'react-dom';
import {
  Tabs,
  Tab,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  SelectChangeEvent,
} from '@mui/material';
import { useOperatorsStore } from '../store/loadingStore';

interface HeaderProps {
  logo: string;
  filters: string[];
  subfilters: string[];
  operators: string[];
  timeperiods: string[];
  onFilterChange: (filter: string) => void;
  hideFilters: boolean;
  info: string;
  onPeriodChange: (period: string) => void;
  onSubfilterChange: (subfilter: string) => void;
  disableTimeperiods: boolean;
  disableOGType: boolean;
  enableOperators: boolean;
  tabsPortal?: React.ReactNode;
}

const Header: React.FC<HeaderProps> = React.memo(
  ({
    logo,
    filters,
    subfilters,
    operators,
    timeperiods,
    onFilterChange,
    info,
    onPeriodChange,
    onSubfilterChange,
    hideFilters,
    disableTimeperiods,
    disableOGType = false,
    enableOperators = false,
    tabsPortal,
  }) => {
    const [selectedFilter, setSelectedFilter] = useState(0);
    const [selectedTimePeriod, setSelectedTimePeriod] = useState(
      timeperiods[0],
    );
    const [selectedSubfilter, setSelectedSubfilter] = useState(subfilters[0]);
    const selectedOperator = useOperatorsStore(
      (state) => state.selectedOperator,
    );

    const setSelectedOperator = useOperatorsStore(
      (state) => state.setSelectedOperator,
    );

    const portalContainerRef = useRef<HTMLDivElement | null>(null);
    const [isPortalReady, setIsPortalReady] = useState(false);

    const handleTabChange = useCallback(
      (event: React.ChangeEvent<{}>, newValue: number) => {
        setSelectedFilter(newValue);
      },
      [],
    );

    const handleTimePeriodChange = useCallback(
      (event: SelectChangeEvent<string>) => {
        setSelectedTimePeriod(event.target.value);
        onPeriodChange(event.target.value);
      },
      [onPeriodChange],
    );

    const handleOperatorsChange = useCallback(
      (event: SelectChangeEvent<string>) => {
        setSelectedOperator(event.target.value);
      },
      [setSelectedOperator],
    );

    const handleSubfilterChange = useCallback(
      (event: SelectChangeEvent<string>) => {
        setSelectedSubfilter(event.target.value);
        onSubfilterChange(event.target.value);
      },
      [onSubfilterChange],
    );

    useEffect(() => {
      onFilterChange(filters[selectedFilter]);
    }, [selectedFilter, filters, onFilterChange]);

    useEffect(() => {
      if (portalContainerRef.current) {
        setIsPortalReady(true);
      }
    }, [portalContainerRef.current]);

    return (
      <>
        <div
          className="header"
          style={{
            width: '100%',
            position: 'fixed',
            top: 0,
            zIndex: 1001,
            backgroundColor: 'white',
            display: 'flex',
            alignItems: 'center',
            padding: '0 8px 6px 8px',
            boxShadow: '0 2px 4px rgba(0, 0, 0, 0.1)',
            borderBottom: '1px solid #ddd',
          }}
        >
          <div
            className="logo"
            style={{
              display: 'flex',
              alignItems: 'flex-start',
              flexDirection: 'row',
              marginTop: '8px',
            }}
          >
            <img
              src={logo}
              alt="logo"
              style={{ maxWidth: '150px', marginRight: '10px' }}
            />
            <span
              style={{
                fontSize: '24px',
                fontWeight: 'normal',
                textTransform: 'uppercase',
                whiteSpace: 'nowrap',
                color: '#242424',
                lineHeight: '24px',
                marginLeft: '16px',
              }}
            >
              AMI Data Explorer
            </span>
          </div>

          <div
            ref={portalContainerRef}
            style={{
              marginLeft: 'auto',
              display: 'flex',
              alignItems: 'center',
              marginRight: '10px',
              marginTop: '6px',
            }}
          ></div>

          {hideFilters && (
            <div
              style={{
                position: 'absolute',
                top: '60px',
                left: '7px',
                width: '100%',
                display: 'flex',
                flexWrap: 'nowrap',
                alignItems: 'center',
                flexDirection: 'row',
                justifyContent: 'flex-start',
                zIndex: '2000',
                paddingBottom: '5px',
                backgroundColor: '#fff',
              }}
            >
              <div className="filters" style={{ marginLeft: '20px' }}>
                <Tabs
                  value={selectedFilter}
                  onChange={handleTabChange}
                  aria-label="filter tabs"
                >
                  {filters.map((filter, index) => (
                    <Tab label={filter} key={index} />
                  ))}
                </Tabs>
              </div>

              <div
                className="subfilters"
                style={{ marginLeft: '20px', display: 'none' }}
              >
                <FormControl fullWidth>
                  <InputLabel>O&amp;G Facility Type</InputLabel>
                  <Select
                    label="Subfilters"
                    value={disableOGType ? subfilters[0] : selectedSubfilter}
                    onChange={handleSubfilterChange}
                    fullWidth
                    disabled={disableOGType}
                  >
                    {subfilters.map((subfilter, index) => (
                      <MenuItem value={subfilter} key={index}>
                        {subfilter}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              <div className="time" style={{ marginLeft: '20px' }}>
                <FormControl fullWidth>
                  <InputLabel>Time Periods</InputLabel>
                  <Select
                    label="Time Periods"
                    value={
                      disableTimeperiods ? timeperiods[0] : selectedTimePeriod
                    }
                    onChange={handleTimePeriodChange}
                    fullWidth
                    disabled={disableTimeperiods}
                  >
                    {timeperiods.map((period, index) => (
                      <MenuItem value={period} key={index}>
                        {period}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </div>

              {operators && operators.length > 0 && enableOperators && (
                <div className="time" style={{ marginLeft: '20px' }}>
                  <FormControl fullWidth>
                    <InputLabel>Operator ID</InputLabel>
                    <Select
                      label="Operator ID"
                      value={selectedOperator}
                      onChange={handleOperatorsChange}
                      fullWidth
                    >
                      {operators.map((operator, index) => (
                        <MenuItem value={operator} key={index}>
                          {operator}
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </div>
              )}
            </div>
          )}
          {info && (
            <div className="info" style={{ marginLeft: '20px' }}>
              {info}
            </div>
          )}
        </div>

        {tabsPortal &&
          isPortalReady &&
          portalContainerRef.current &&
          ReactDOM.createPortal(tabsPortal, portalContainerRef.current)}
      </>
    );
  },
);

export default Header;
